// eventCategory.js — the ONE rule that puts a case in an Event filter bucket.
//
// Authored under the API (so it ships in the Functions deploy package) and
// bundled into the SPA through the `@shared` alias, exactly like configSchema
// and roleSchema. Both sides must agree because the bucket keys ARE the
// `?treatment=` values in the case-list URL: an aggregate that counted cases
// into different buckets than the list filters by would send someone to a
// filtered list whose count contradicted the number they clicked.
//
// RUNTIME-AGNOSTIC HARD RULE: pure functions only — no DB, Node or browser APIs.
// The by-clinic rollup buckets in JS rather than in SQL for the same reason: a
// second copy of this cascade in T-SQL would drift silently.

/** The bucket keys, in display order. Same strings as `?treatment=` accepts. */
export const EVENT_CATEGORY_KEYS = ['lhr', 'awi', 'other', 'nearMiss', 'clinicIncident'];

export const EVENT_CATEGORY_LABEL = {
  lhr: 'LHR',
  awi: 'AWI',
  other: 'Other',
  nearMiss: 'Near miss',
  clinicIncident: 'Clinic incident',
};

/**
 * Categorise a case for the Event filter group.
 *
 * Accepts either the API DTO shape (`eventType` / `treatment`) or a raw `cases`
 * row (`event_type` / `department`) — `department` has meant treatment_type
 * since migration 007, and the DTO renames it to `treatment`.
 *
 * @returns {'lhr'|'awi'|'other'|'nearMiss'|'clinicIncident'|null} null when a
 *   Treatment case carries no treatment type at all (uncategorised — it is
 *   counted in the totals but sits in no bucket, which is what the list's
 *   `cat && !filterTreatments[cat]` check already does).
 */
export function eventCategoryOf(c) {
  const eventType = c.eventType ?? c.event_type;
  if (eventType === 'NearMiss') return 'nearMiss';
  if (eventType === 'ClinicIncident') return 'clinicIncident';
  const tx = String(c.treatment ?? c.department ?? '').toUpperCase();
  if (tx === 'LHR' || tx.includes('LASER')) return 'lhr';
  if (tx === 'AWI' || tx.includes('INJECTABLE') || tx.includes('BOTOX') || tx.includes('FILLER')) return 'awi';
  if (tx) return 'other';
  return null;
}

/** An all-zero bucket map — the fail-closed base every rollup builds on. */
export function emptyEventCategoryCounts() {
  const out = {};
  for (const k of EVENT_CATEGORY_KEYS) out[k] = 0;
  return out;
}
